const INDICATOR_ID = "ft-scroll-indicator";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const parsePx = (value) => {
  const parsed = Number.parseFloat(String(value).trim().replace("px", ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getStickyHeaderGroupHeight = () => {
  const styles = getComputedStyle(document.documentElement);
  const stickyArea = parsePx(styles.getPropertyValue("--sticky-area-height"));
  const activeOfferIsSticky = parsePx(styles.getPropertyValue("--active-offer-bar-is-sticky"));
  const activeOfferHeight = parsePx(styles.getPropertyValue("--active-offer-bar-height"));
  const activeOfferStickyHeight = activeOfferIsSticky > 0 ? activeOfferHeight : 0;

  const totalStickyHeight = stickyArea + activeOfferStickyHeight;
  if (totalStickyHeight > 0) return totalStickyHeight;

  return parsePx(styles.getPropertyValue("--header-height"));
};

const getScrollMax = () => {
  const { documentElement } = document;
  return Math.max(0, documentElement.scrollHeight - window.innerHeight);
};

const isVisibleElement = (element) => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const getSectionElements = () => {
  const explicit = Array.from(document.querySelectorAll("[data-ft-scroll-section]"));
  if (explicit.length > 0) return explicit;

  const main = document.getElementById("main");
  if (!main) return [];

  const shopifySections = Array.from(main.children).filter(
    (el) => el instanceof HTMLElement && el.id?.startsWith("shopify-section-"),
  );
  if (shopifySections.length > 0) return shopifySections;

  return Array.from(main.querySelectorAll("section[id], h2[id], h3[id]"));
};

const buildSections = () => {
  const elements = getSectionElements()
    .filter((el) => el instanceof HTMLElement)
    .filter((el) => !el.hasAttribute("data-ft-scroll-ignore"))
    .filter((el) => isVisibleElement(el))
    .filter((el) => (el.offsetHeight || 0) >= 80);

  const sections = elements
    .map((element) => ({
      element,
      top: element.getBoundingClientRect().top + window.scrollY,
    }))
    .sort((a, b) => a.top - b.top);

  // De-dupe any near-identical tops (some sections can be nested/overlapping).
  const deduped = [];
  for (const section of sections) {
    const last = deduped[deduped.length - 1];
    if (!last || Math.abs(last.top - section.top) > 8) {
      deduped.push(section);
    }
  }

  return deduped;
};

const main = () => {
  const indicator = document.getElementById(INDICATOR_ID);
  if (!indicator) return;

  const bar = indicator.querySelector("[data-ft-scroll-indicator-bar]");
  const markersEl = indicator.querySelector("[data-ft-scroll-indicator-markers]");
  if (!bar || !markersEl) return;

  let sections = [];
  let sectionTops = [];
  let rafId = null;

  const renderMarkers = () => {
    markersEl.replaceChildren();
    sections = buildSections();
    sectionTops = sections.map((s) => s.top);

    const scrollMax = getScrollMax();
    if (scrollMax < 200 || sections.length < 2) {
      indicator.hidden = true;
      return;
    }

    indicator.hidden = false;

    for (const section of sections) {
      const ratio = scrollMax > 0 ? clamp(section.top / scrollMax, 0, 1) : 0;
      const marker = document.createElement("div");
      marker.className = "ft-scroll-indicator__marker";
      marker.style.top = `${ratio * 100}%`;
      markersEl.appendChild(marker);
    }
  };

  const update = () => {
    rafId = null;

    const scrollMax = getScrollMax();
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    const progress = scrollMax > 0 ? clamp(scrollY / scrollMax, 0, 1) : 0;
    indicator.style.setProperty("--ft-scroll-progress", String(progress));

    const stickyHeaderGroupHeight = getStickyHeaderGroupHeight();
    const activationPoint = scrollY + stickyHeaderGroupHeight + 8;

    const markers = Array.from(markersEl.children);
    for (let i = 0; i < markers.length; i += 1) {
      const isActive = activationPoint >= (sectionTops[i] ?? Infinity);
      markers[i].classList.toggle("is-active", isActive);
    }
  };

  const scheduleUpdate = () => {
    if (rafId != null) return;
    rafId = window.requestAnimationFrame(update);
  };

  const rebuild = () => {
    renderMarkers();
    scheduleUpdate();
  };

  renderMarkers();
  update();

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", rebuild, { passive: true });
  window.addEventListener("load", rebuild, { passive: true });

  // Ensure markers stay correct if the theme lazy-loads content/images later.
  const mainEl = document.getElementById("main");
  if (mainEl && "ResizeObserver" in window) {
    const ro = new ResizeObserver(() => rebuild());
    ro.observe(mainEl);
  }
};

// Defer the initial layout reads (getBoundingClientRect / offsetHeight in
// buildSections) to after the first paint so they don't force a synchronous
// reflow during DOMContentLoaded processing.
const deferredMain = () => window.requestAnimationFrame(main);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", deferredMain, { once: true });
} else {
  deferredMain();
}
